import { faArrowCircleLeft, faArrowCircleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DescriptionIcon from "@mui/icons-material/Description";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Box, CircularProgress } from "@mui/material";
import axios from 'axios';
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import usePermissions from "../../../../Hooks/usePermissions";
import { Button, Modal } from "react-bootstrap";
import {
  GeoJSON,
  LayersControl,
  MapContainer,
  TileLayer,
  useMap,
  WMSTileLayer,
} from "react-leaflet";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment-timezone";
import CommonTable from "../../../common/CommonTable.js";
import InformationFR from "./Information_FR.js";
import { translations } from "./Translations.js";
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";
import {
  preloadReportLogos, COLORS, HEADER_H,
} from "../../../common/reportTemplate/reportTemplate.js";
import {
  drawFireHeader, drawFireSummaryCard, drawRiskCountCards, drawTwoToneHeading, drawFireFooter,
  formatIssuedAt, FR_CONTENT_BOTTOM,
} from "./pdfLayout_FR.js";
// import { Form } from "react-bootstrap";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const FIRE_RISK_API = "https://drr-dss-backend.rimes.int/firerisk/get-firerisk-data/";

const INITIAL_VIEW = {
  lat: -8.8742,
  lng: 125.7275,
  zoom: 8,
};

const mapAdvisoryToRiskLevel = (advisoryText) => {
  if (!advisoryText) return 'N/A';
  const clean = advisoryText.trim().toLowerCase();
  if (clean.includes('extreme')) return 'Extreme Risk';
  if (clean.includes('high')) return 'High Risk';
  if (clean.includes('moderate')) return 'Moderate Risk';
  if (clean.includes('low')) return 'Low Risk';
  return 'N/A';
};

const formatDateForDisplay = (dateString, offset) => {
  if (!dateString) return "--";
  return `${dateString}_UTC00 + ${offset}d : ${dateString}[UTC00-24]`;
};

// Build WMS layer name from init_time + forecast_date
const buildWmsLayerName = (initTime, forecastDate) => {
  if (!initTime || !forecastDate) return null;
  return `fire-risk:final_fire_risk_fs_${initTime}_${forecastDate}`;
};

// Get current Timor-Leste date (Asia/Dili timezone)
const getCurrentTimorDate = () => {
  return moment.tz("Asia/Dili").format("YYYY-MM-DD");
};

// Parse init_time format (e.g., "20260519_00") to date
const parseInitTime = (initTime) => {
  if (!initTime || initTime.length < 8) return null;
  const dateStr = initTime.substring(0, 8); // "20260519"
  return moment(dateStr, "YYYYMMDD").format("YYYY-MM-DD");
};

// ─── Fetch + transform API data ──────────────────────────────────────────────
const fetchAllForecastData = async () => {
  try {
    const [fireRiskResp, munisResp, subMunisResp, sucosResp] = await Promise.all([
      axios.get(FIRE_RISK_API),
      axios.post(`${backendUrl}Municipalities/municipalities_list/`),
      axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`),
      axios.post(`${backendUrl}Municipalities/suco_list/`),
    ]);

    const apiPayload = fireRiskResp.data;
    const latestInitTime = apiPayload.latest_init_time || "";
    const rawData = apiPayload.data || {};

    const munisData = munisResp.data;
    const subMunisData = subMunisResp.data;
    const sucosData = sucosResp.data;

    const muniMap = new Map(munisData.map(m => [m.adm1_code, m.adm1]));
    const subMuniMap = new Map(subMunisData.map(s => [s.adm2_code, s.adm2]));

    const allDatesSet = new Set();
    Object.values(rawData).forEach(arr => {
      arr.forEach(item => allDatesSet.add(item.forecast_date));
    });
    const forecastDates = Array.from(allDatesSet).sort();

    const fullStatsData = {};
    Object.entries(rawData).forEach(([adm3Code, arr]) => {
      fullStatsData[adm3Code] = {};
      arr.forEach(item => {
        fullStatsData[adm3Code][item.forecast_date] = item;
      });
    });

    const processedData = {};
    sucosData.forEach(suco => {
      const adm3Code = suco.adm3_code;
      const adm1Code = adm3Code.substring(0, 4);
      const adm2Code = adm3Code.substring(0, 6);

      processedData[adm3Code] = {
        adm1_code: adm1Code,
        adm2_code: adm2Code,
        adm3_code: adm3Code,
        Municipality: muniMap.get(adm1Code) || 'N/A',
        PostAdmin: subMuniMap.get(adm2Code) || 'N/A',
        Suco: suco.adm3 || adm3Code,
        'Total Population': '0',
        'Total Male': '0',
        'Total Female': '0',
        'Household': '0',
      };

      forecastDates.forEach(date => {
        processedData[adm3Code][`Characteristic_${date}`] = 'N/A';
        processedData[adm3Code][`advisory_${date}`] = 'N/A';
        processedData[adm3Code][`low_${date}`] = 0;
        processedData[adm3Code][`moderate_${date}`] = 0;
        processedData[adm3Code][`high_${date}`] = 0;
        processedData[adm3Code][`extreme_${date}`] = 0;
      });

      const sucoEntries = rawData[adm3Code] || [];
      sucoEntries.forEach(item => {
        const date = item.forecast_date;
        processedData[adm3Code][`Characteristic_${date}`] = mapAdvisoryToRiskLevel(item.advisory);
        processedData[adm3Code][`advisory_${date}`] = item.advisory || 'N/A';
        processedData[adm3Code][`low_${date}`] = item.low || 0;
        processedData[adm3Code][`moderate_${date}`] = item.moderate || 0;
        processedData[adm3Code][`high_${date}`] = item.high || 0;
        processedData[adm3Code][`extreme_${date}`] = item.extreme || 0;

        if (item.adm1) processedData[adm3Code].Municipality = item.adm1;
        if (item.adm2) processedData[adm3Code].PostAdmin = item.adm2;
        if (item.adm3) processedData[adm3Code].Suco = item.adm3;
      });
    });

    const finalData = Object.values(processedData).map((item, idx) => ({ ...item, SlNo: idx + 1 }));

    return {
      data: finalData,
      error: null,
      fullStatsData,
      forecastDates,
      latestInitTime,
      munisData,
      subMunisData,
      sucosData,
    };
  } catch (error) {
    console.error("Failed to fetch data:", error);
    return {
      data: [],
      error: error.message,
      fullStatsData: {},
      forecastDates: [],
      latestInitTime: "",
      munisData: [],
      subMunisData: [],
      sucosData: [],
    };
  }
};

// ─── Map helpers ─────────────────────────────────────────────────────────────
const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.flyToBounds(bounds);
  }, [map, bounds]);
  return null;
};

const ZoomToFeature = ({ feature }) => {
  const map = useMap();
  useEffect(() => {
    if (feature) {
      const layer = L.geoJSON(feature);
      map.flyToBounds(layer.getBounds(), { padding: [20, 20] });
    }
  }, [feature, map]);
  return null;
};

const getBoundaryStyle = () => ({ color: "black", weight: 1, fillOpacity: 0 });

const selectedGeojsonStyle = (feature, selectedSuco, selectedSubMunicipality, selectedMunicipality) => {
  if (feature.properties.ADM_Code && feature.properties.ADM_Code === selectedSuco)
    return { color: "white", weight: 3, fillOpacity: 0 };
  if (feature.properties.ADM2_Code && feature.properties.ADM2_Code === selectedSubMunicipality)
    return { color: "white", weight: 3, fillOpacity: 0 };
  if (feature.properties.ADM1_Code && feature.properties.ADM1_Code === selectedMunicipality)
    return { color: "white", weight: 3, fillOpacity: 0 };
  return getBoundaryStyle();
};

// ─── Column definition builder for CommonTable ───────────────────────────────
const buildTableColumns = (selectedDate, legendColors) => {
  if (!selectedDate) return [];

  return [
    { key: "SlNo", label: "S.No", sortable: false, align: "center" },
    { key: "Municipality", label: "Municipality", sortable: true },
    { key: "PostAdmin", label: "Post Admin", sortable: true },
    { key: "Suco", label: "Suco", sortable: true },
    {
      key: "forecast_date",
      label: "Forecast Date",
      sortable: true,
      align: "center",
      render: (row) => row.forecast_date || selectedDate || "--",
    },
    {
      key: `low_${selectedDate}`,
      label: "Low (%)",
      sortable: true,
      align: "right",
      render: (row) => {
        const val = row[`low_${selectedDate}`];
        return val !== undefined ? Number(val).toFixed(2) : '0.00';
      },
    },
    {
      key: `moderate_${selectedDate}`,
      label: "Moderate (%)",
      sortable: true,
      align: "right",
      render: (row) => {
        const val = row[`moderate_${selectedDate}`];
        return val !== undefined ? Number(val).toFixed(2) : '0.00';
      },
    },
    {
      key: `high_${selectedDate}`,
      label: "High (%)",
      sortable: true,
      align: "right",
      render: (row) => {
        const val = row[`high_${selectedDate}`];
        return val !== undefined ? Number(val).toFixed(2) : '0.00';
      },
    },
    {
      key: `extreme_${selectedDate}`,
      label: "Extreme (%)",
      sortable: true,
      align: "right",
      render: (row) => {
        const val = row[`extreme_${selectedDate}`];
        return val !== undefined ? Number(val).toFixed(2) : '0.00';
      },
    },
    {
      key: `advisory_${selectedDate}`,
      label: "Advisory",
      sortable: true,
      render: (row) => {
        const val = row[`advisory_${selectedDate}`] || "N/A";
        return (
          <span
            style={{
              background: legendColors[val] || legendColors["N/A"],
              padding: "2px 8px",
              borderRadius: "4px",
              fontWeight: 700,
              fontSize: "12px",
              color: "black",
              display: "inline-block",
              minWidth: "110px",
              textAlign: "center",
            }}
          >
            {val}
          </span>
        );
      },
    },
  ];
};

// ─── Image loader utility ────────────────────────────────────────────────────
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("Invalid image source"));
      return;
    }
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => {
      console.warn("Image load failed:", e);
      reject(e);
    };
    img.src = src;
  });
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Forecast = () => {
  const { hasPermission } = usePermissions();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [municipalityData, setMunicipalityData] = useState(null);
  const [subMunicipalityData, setSubMunicipalityData] = useState(null);
  const [sucoData, setSucoData] = useState(null);
  const [fireRiskTableData, setFireRiskTableData] = useState([]);
  const [fullFireRiskData, setFullFireRiskData] = useState({});
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  // Risk levels included in the PDF's key table (Extreme + High by default).
  const [reportRiskLevels, setReportRiskLevels] = useState(["Extreme Risk", "High Risk"]);
  const mapRef = useRef(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [isDataReady, setIsDataReady] = useState(false);
  const [latestInitTime, setLatestInitTime] = useState("");

  const [, setMunicipalities] = useState([]);
  const [allSubMunicipalities, setAllSubMunicipalities] = useState([]);
  const [allSucos, setAllSucos] = useState([]);
  const [, setSubMunicipalities] = useState([]);
  const [, setSucos] = useState([]);
  const [selectedMunicipality] = useState('');
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState('');
  const [selectedSuco, setSelectedSuco] = useState('');
  const [adm1Data, setAdm1Data] = useState(null);
  const [adm2Data, setAdm2Data] = useState(null);
  const [adm3Data, setAdm3Data] = useState(null);
  const [zoomFeature, setZoomFeature] = useState(null);
  const [, setInitialMapBounds] = useState(null);
  const [forecastDates, setForecastDates] = useState([]);

  const colorMap = useMemo(() => ({
    'Low Risk': [148, 203, 85],
    'Moderate Risk': [243, 235, 18],
    'High Risk': [243, 155, 58],
    'Extreme Risk': [230, 45, 58],
    'N/A': [222, 222, 223],
  }), []);

  const legendColors = useMemo(() => ({
    "Extreme Risk": "rgb(230, 45, 58)",
    "High Risk": "rgb(243, 155, 58)",
    "Moderate Risk": "rgb(243, 235, 18)",
    "Low Risk": "rgb(148, 203, 85)",
    "N/A": "rgb(222,222,223)",
    All: "#e2e8f0",
  }), []);

  const fullStatsMap = useMemo(() => {
    const map = new Map();
    Object.keys(fullFireRiskData).forEach(adm3Code => {
      map.set(adm3Code, fullFireRiskData[adm3Code]);
    });
    return map;
  }, [fullFireRiskData]);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        const resp = await fetchAllForecastData();
        const { data, fullStatsData, forecastDates: dates, latestInitTime: initTime, munisData, subMunisData, sucosData } = resp;

        const [municipalityGeo, subMunicipalityGeo, sucoGeo] = await Promise.all([
          fetch("/DATA/OSMbased_adm1_boundary.json").then(r => r.json()),
          fetch("/DATA/OSMbased_adm2_boundary.json").then(r => r.json()),
          fetch("/DATA/OSMbased_adm3_boundary.json").then(r => r.json()),
        ]);

        setAdm1Data(municipalityGeo);
        setAdm2Data(subMunicipalityGeo);
        setAdm3Data(sucoGeo);
        setMunicipalityData(municipalityGeo);
        setSubMunicipalityData(subMunicipalityGeo);
        setSucoData(sucoGeo);

        setMunicipalities(munisData);
        setAllSubMunicipalities(subMunisData);
        setAllSucos(sucosData);
        setSubMunicipalities(subMunisData);
        setSucos(sucosData);

        const sucoPopMap = new Map();
        sucoGeo.features.forEach(f => {
          sucoPopMap.set(f.properties.ADM_Code, {
            Population: f.properties.Population,
            Male: f.properties.Male,
            Female: f.properties.Female,
            Household: f.properties.Household,
          });
        });

        const updatedData = data.map(item => {
          const pop = sucoPopMap.get(item.adm3_code);
          if (pop) {
            return {
              ...item,
              'Total Population': pop.Population,
              'Total Male': pop.Male,
              'Total Female': pop.Female,
              'Household': pop.Household,
            };
          }
          return item;
        });

        setFireRiskTableData(updatedData);
        setFullFireRiskData(fullStatsData);
        setForecastDates(dates);
        setLatestInitTime(initTime);

        if (dates.length > 0) setSelectedDate(dates[0]);

        if (municipalityGeo?.features?.length > 0) {
          const geoLayer = L.geoJSON(municipalityGeo);
          const bounds = geoLayer.getBounds();
          setMapBounds(bounds);
          setInitialMapBounds(bounds);
        }

        setIsDataReady(true);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setIsDataReady(false);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // ── Filtered table data ───────────────────────────────────────────────────
  const filteredFireRiskData = useMemo(() => {
    let d = fireRiskTableData;
    if (selectedMunicipality) d = d.filter(i => i.adm1_code === selectedMunicipality);
    if (selectedSubMunicipality) d = d.filter(i => i.adm2_code === selectedSubMunicipality);
    if (selectedSuco) d = d.filter(i => i.adm3_code === selectedSuco);
    return d.sort(
      (a, b) =>
        (a.Municipality || '').localeCompare(b.Municipality || '') ||
        (a.PostAdmin || '').localeCompare(b.PostAdmin || '') ||
        (a.Suco || '').localeCompare(b.Suco || '')
    );
  }, [fireRiskTableData, selectedMunicipality, selectedSubMunicipality, selectedSuco]);

  const tableDataWithAdvisory = useMemo(() => {
    return filteredFireRiskData.map(row => ({
      ...row,
      advisory: row[`advisory_${selectedDate}`] || 'N/A',
      forecast_date: selectedDate || '--',
      [`low_${selectedDate}`]: row[`low_${selectedDate}`] ?? 0,
      [`moderate_${selectedDate}`]: row[`moderate_${selectedDate}`] ?? 0,
      [`high_${selectedDate}`]: row[`high_${selectedDate}`] ?? 0,
      [`extreme_${selectedDate}`]: row[`extreme_${selectedDate}`] ?? 0,
      [`advisory_${selectedDate}`]: row[`advisory_${selectedDate}`] || 'N/A',
    }));
  }, [filteredFireRiskData, selectedDate]);

  // ── Location filter cascades ───────────────────────────────────────────────
  useEffect(() => {
    if (selectedMunicipality) {
      setSubMunicipalities(allSubMunicipalities.filter(s => s.adm1_code === selectedMunicipality));
      setSucos([]);
      setSelectedSubMunicipality('');
      setSelectedSuco('');
    } else {
      setSubMunicipalities(allSubMunicipalities);
      setSucos(allSucos);
      setSelectedSubMunicipality('');
      setSelectedSuco('');
    }
  }, [selectedMunicipality, allSubMunicipalities, allSucos]);

  useEffect(() => {
    if (selectedSubMunicipality) {
      setSucos(allSucos.filter(s => s.adm2_code === selectedSubMunicipality));
      setSelectedSuco('');
    } else {
      setSucos(allSucos);
      setSelectedSuco('');
    }
  }, [selectedSubMunicipality, allSucos]);

  // ── Zoom on selection ─────────────────────────────────────────────────────
  useEffect(() => {
    let feature = null;
    if (selectedSuco && adm3Data)
      feature = adm3Data.features.find(f => f.properties.ADM_Code === selectedSuco);
    else if (selectedSubMunicipality && adm2Data)
      feature = adm2Data.features.find(f => f.properties.ADM_Code === selectedSubMunicipality);
    else if (selectedMunicipality && adm1Data)
      feature = adm1Data.features.find(f => f.properties.ADM_Code === selectedMunicipality);
    setZoomFeature(feature);
  }, [selectedMunicipality, selectedSubMunicipality, selectedSuco, adm1Data, adm2Data, adm3Data]);

  // ── Date navigation ───────────────────────────────────────────────────────
  const handlePrevDate = () => {
    if (selectedDateIndex > 0) {
      const newIdx = selectedDateIndex - 1;
      setSelectedDateIndex(newIdx);
      setSelectedDate(forecastDates[newIdx]);
    }
  };

  const handleNextDate = () => {
    if (selectedDateIndex < forecastDates.length - 1) {
      const newIdx = selectedDateIndex + 1;
      setSelectedDateIndex(newIdx);
      setSelectedDate(forecastDates[newIdx]);
    }
  };

  // ── Map styling ────────────────────────────────────────────────────────────
  const getSucoStyle = useCallback((feature) => {
    let style = {
      color: "gray",
      weight: 1,
      fillOpacity: 0,
      fillColor: "transparent"
    };

    if (feature.properties.ADM_Code === selectedSuco) {
      style.color = "white";
      style.weight = 3;
    } else if (feature.properties.ADM2_Code === selectedSubMunicipality) {
      style.color = "white";
      style.weight = 2;
    }

    return style;
  }, [selectedSuco, selectedSubMunicipality]);

  const onEachSucoFeature = useCallback((feature, layer) => {
    const { ADM_Code, ADM3, ADM1, ADM2 } = feature.properties;
    const sucoStatsByDate = fullStatsMap.get(ADM_Code);
    const dateStats = sucoStatsByDate ? sucoStatsByDate[selectedDate] : null;

    const advisoryText = dateStats?.advisory || 'N/A';

    const riskLevel = mapAdvisoryToRiskLevel(advisoryText);
    const colorRgbArray = colorMap[riskLevel] || colorMap['N/A'];
    const color = `rgb(${colorRgbArray.join(',')})`;

    layer.bindPopup(`
      <div style="font-family: Arial, sans-serif; font-size: 13px; max-width:300px;">
        <h3 style="margin-bottom:8px; border-bottom: 2px solid #0d6efd; padding-bottom: 4px;">Suco: <b>${ADM3}</b></h3>
        <div style="font-size:12px; line-height: 1.6;">
          <b>Municipality:</b> ${ADM1}<br/>
          <b>Post Admin:</b> ${ADM2}<br/>
          <b>Date:</b> ${selectedDate}<br/>
          <b>Advisory:</b>
          <span style="background-color:${color};padding:4px 8px;border-radius:4px;font-weight:bold;color:black;display:inline-block;text-align:center;min-width:60px;margin-top:2px;">
            ${advisoryText}
          </span>
        </div>
      </div>
    `);
  }, [fullStatsMap, selectedDate, colorMap]);

  // ─── REPORT GENERATION ────────────────────────────────────────────────────
  const handleGenerateReportClick = () => {
    if (!isDataReady) {
      alert("Please wait until all data is loaded and report generation is possible.");
      return;
    }
    setShowLanguageModal(true);
  };

  const toggleReportRiskLevel = (level) => {
    setReportRiskLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const handleLanguageSelectAndGenerate = (selectedLanguage) => {
    if (reportRiskLevels.length === 0) {
      alert("Please select at least one risk level for the report.");
      return;
    }
    setShowLanguageModal(false);
    captureAndGeneratePdf(selectedLanguage, reportRiskLevels);
  };

  const captureAndGeneratePdf = async (language, selectedRiskLevels = ["Extreme Risk", "High Risk"]) => {
    if (!mapRef.current) {
      alert("PDF generation failed: map not ready. Please refresh and try again.");
      return;
    }

    setLoading(true);

    try {
      // ─────────────────────────────────────────────────────────
      // PDF SETUP
      // ─────────────────────────────────────────────────────────
      const pdf = new jsPDF("portrait", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const sideMargin = 10;
      const headerHeight = HEADER_H + 5;
      // Content may run down to just above the footer bar.
      const availablePgH = FR_CONTENT_BOTTOM - headerHeight;

      const t = translations[language];

      // ─────────────────────────────────────────────────────────
      // BRANDED HEADER (replaces the old banner.png)
      // ─────────────────────────────────────────────────────────
      const frHeaderOpts = {
        title: "TIMOR LESTE FIRE RISK ADVISORY",
        issuedBy: "DNMG-APC",
        issuedAt: formatIssuedAt(),
      };
      const frLogos = await preloadReportLogos();

      const addBannerSafe = () => {
        drawFireHeader(pdf, frLogos, frHeaderOpts);
        return headerHeight;
      };

      const addPageWithHeader = () => {
        pdf.addPage();
        return addBannerSafe() + 5;
      };

      // ─────────────────────────────────────────────────────────
      // DETERMINE REPORT DATE LOGIC
      // ─────────────────────────────────────────────────────────
      const currentTimorDate = getCurrentTimorDate();
      const initTimeDate = parseInitTime(latestInitTime);

      // For WMS image: use initTimeDate (matches the layer naming)
      const reportDateForImage = initTimeDate || currentTimorDate;

      // For table data: use currentTimorDate
      const reportDateForTable = currentTimorDate;

      // ─────────────────────────────────────────────────────────
      // RISK SUMMARY COUNTS (using table report date)
      // ─────────────────────────────────────────────────────────
      const riskCounts = { 'Extreme Risk': 0, 'High Risk': 0, 'Moderate Risk': 0, 'Low Risk': 0, 'N/A': 0 };

      try {
        filteredFireRiskData.forEach((item) => {
          const advisory = item[`advisory_${reportDateForTable}`] || "N/A";
          const riskLevel = mapAdvisoryToRiskLevel(advisory);
          riskCounts[riskLevel] = (riskCounts[riskLevel] || 0) + 1;
        });
      } catch (e) {
        console.warn("Risk counts failed:", e);
      }


      // ─────────────────────────────────────────────────────────
      // PAGE 1 — HEADER / OVERVIEW / SUMMARY TABLE
      // ─────────────────────────────────────────────────────────
      let currentY = addBannerSafe() + 3;

      const levelText = selectedSuco ? "Suco Level"
        : selectedSubMunicipality ? "Post Admin Level"
        : selectedMunicipality ? "Municipality Level"
        : "Country Level";
      currentY = drawFireSummaryCard(pdf, currentY, {
        forecastDate: moment(reportDateForTable).isValid()
          ? moment(reportDateForTable).format("DD MMMM YYYY")
          : (reportDateForTable || "N/A"),
        levelText,
      });
      currentY += 4;

      // ─────────────────────────────────────────────────────────
      // MAP IMAGE — WMS GetMap (same pattern as landslide component)
      // ─────────────────────────────────────────────────────────
      // Frame runs to -9.75 so the south coast isn't cropped; the image is
      // sized to the frame's true proportions (4.0 x 1.75 degrees).
      const MAP_W = 1200;
      const MAP_H = 525;
      let mapImageDataUrl = null;

      try {
        const WMS_BASE = "https://drr-dss.rimes.int/fire_risk/wms";
        const BBOX = "123.5,-9.75,127.5,-8.0";

        const layerName = buildWmsLayerName(latestInitTime, reportDateForImage);
        // console.log("[FireRisk PDF] WMS layer name:", layerName);
        // console.log("[FireRisk PDF] latestInitTime:", latestInitTime, "| reportDateForImage:", reportDateForImage);

        let fireImg = null;

        if (layerName) {
          const wmsParams = new URLSearchParams({
            service: "WMS",
            version: "1.1.0",
            request: "GetMap",
            layers: layerName,
            bbox: BBOX,
            width: String(MAP_W),
            height: String(MAP_H),
            srs: "EPSG:4326",
            format: "image/png",
            transparent: "true",
          });

          const wmsUrl = `${WMS_BASE}?${wmsParams.toString()}`;
          // console.log("[FireRisk PDF] Fetching WMS URL:", wmsUrl);

          try {
            const res = await fetch(wmsUrl);
            // console.log("[FireRisk PDF] WMS response status:", res.status, "| content-type:", res.headers.get("content-type"));

            if (res.ok) {
              const contentType = res.headers.get("content-type") || "";
              if (contentType.includes("image")) {
                const blob = await res.blob();
                const blobUrl = URL.createObjectURL(blob);
                fireImg = await loadImage(blobUrl);
                URL.revokeObjectURL(blobUrl);
                // console.log("[FireRisk PDF] Fire risk image loaded OK");
              } else {
                // Server returned XML/text error instead of image
                const text = await res.text();
                console.warn("[FireRisk PDF] WMS returned non-image content:", text.substring(0, 400));
              }
            } else {
              console.warn("[FireRisk PDF] WMS HTTP error:", res.status);
            }
          } catch (fetchErr) {
            console.warn("[FireRisk PDF] WMS fetch error:", fetchErr);
          }
        } else {
          console.warn("[FireRisk PDF] Could not build layer name — latestInitTime or reportDateForImage is missing");
        }

        // ── Composite on canvas ──────────────────────────────────
        const canvas = document.createElement("canvas");
        canvas.width = MAP_W;
        canvas.height = MAP_H;
        const ctx = canvas.getContext("2d");

        // Background fill
        ctx.fillStyle = "#f2f2f2";
        ctx.fillRect(0, 0, MAP_W, MAP_H);

        // Fire risk WMS layer
        if (fireImg) ctx.drawImage(fireImg, 0, 0, MAP_W, MAP_H);

        // Coordinate projection helper
        const lngLatToPixel = (lng, lat) => {
          const minLng = 123.5, maxLng = 127.5;
          const minLat = -9.75, maxLat = -8.0;
          const x = ((lng - minLng) / (maxLng - minLng)) * MAP_W;
          const y = ((maxLat - lat) / (maxLat - minLat)) * MAP_H;
          return [x, y];
        };

        const drawRing = (ring, context) => {
          if (!ring || ring.length < 2) return;
          const [sx, sy] = lngLatToPixel(ring[0][0], ring[0][1]);
          context.moveTo(sx, sy);
          for (let i = 1; i < ring.length; i++) {
            const [px, py] = lngLatToPixel(ring[i][0], ring[i][1]);
            context.lineTo(px, py);
          }
          context.closePath();
        };

        const drawGeom = (geom, context) => {
          if (!geom) return;
          context.beginPath();
          if (geom.type === "Polygon") {
            geom.coordinates.forEach(ring => drawRing(ring, context));
          } else if (geom.type === "MultiPolygon") {
            geom.coordinates.forEach(poly => poly.forEach(ring => drawRing(ring, context)));
          }
          context.stroke();
        };

        // Suco boundaries (thin)
        if (sucoData?.features) {
          ctx.save();
          ctx.strokeStyle = "rgba(0, 0, 0, 0.6)";
          ctx.lineWidth = 0.5;
          sucoData.features.forEach(f => { if (f.geometry) drawGeom(f.geometry, ctx); });
          ctx.restore();
        }

        // Municipality boundaries (bold)
        if (municipalityData?.features) {
          ctx.save();
          ctx.strokeStyle = "rgba(0, 0, 0, 0.9)";
          ctx.lineWidth = 1.5;
          municipalityData.features.forEach(f => { if (f.geometry) drawGeom(f.geometry, ctx); });
          ctx.restore();
        }

        // Highlight selected area (white outline)
        const highlightFeature =
          (selectedSuco && adm3Data?.features.find(f => f.properties.ADM_Code === selectedSuco)) ||
          (selectedSubMunicipality && adm2Data?.features.find(f => f.properties.ADM_Code === selectedSubMunicipality)) ||
          (selectedMunicipality && adm1Data?.features.find(f => f.properties.ADM_Code === selectedMunicipality)) ||
          null;

        if (highlightFeature?.geometry) {
          ctx.save();
          ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
          ctx.lineWidth = 3;
          drawGeom(highlightFeature.geometry, ctx);
          ctx.restore();
        }

        mapImageDataUrl = canvas.toDataURL("image/png", 0.92);
        // console.log("[FireRisk PDF] Canvas composite complete, data URL length:", mapImageDataUrl.length);

      } catch (e) {
        console.warn("[FireRisk PDF] Map composite failed:", e);
      }

      // ── Embed map in PDF ─────────────────────────────────────
      if (mapImageDataUrl) {
        try {
          const mapW = 158;
          const actualMapH = (MAP_H / MAP_W) * mapW;

          if (currentY + actualMapH + 10 > availablePgH + headerHeight) {
            currentY = addPageWithHeader();
          }

          pdf.addImage(mapImageDataUrl, "PNG", (pageWidth - mapW) / 2, currentY, mapW, actualMapH);
          currentY += actualMapH + 7;
        } catch (e) {
          // console.warn("[FireRisk PDF] Map embed in PDF failed:", e);
        }
      } else {
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "italic");
        pdf.setTextColor(150, 150, 150);
        pdf.text("[Map image could not be generated]", sideMargin, currentY);
        pdf.setTextColor(0, 0, 0);
        currentY += 10;
      }

      // ─────────────────────────────────────────────────────────
      // FIRE RISK LEVELS — suco count cards
      // ─────────────────────────────────────────────────────────
      const limitY = availablePgH + headerHeight;
      if (currentY + 62 > limitY) currentY = addPageWithHeader();
      currentY += 3;
      drawTwoToneHeading(pdf, currentY, "FIRE RISK LEVELS : ", "Suco count by forecast category");
      currentY += 6;

      const RISK_BADGES = [
        { key: "Extreme Risk", label: "EXTREME", bg: COLORS.cardPink, circle: COLORS.riskExtreme },
        { key: "High Risk", label: "HIGH", bg: COLORS.cardOrange, circle: COLORS.riskHigh },
        { key: "Moderate Risk", label: "MODERATE", bg: COLORS.cardTan, circle: COLORS.riskModerate },
        { key: "Low Risk", label: "LOW", bg: COLORS.cardGreen, circle: COLORS.riskLow },
      ];
      currentY = drawRiskCountCards(pdf, currentY, RISK_BADGES.map((b) => ({
        bg: b.bg,
        circle: b.circle,
        heading: b.label,
        count: riskCounts[b.key] || 0,
      })));
      currentY += 9;

      // ─────────────────────────────────────────────────────────
      // KEY TABLE — sucos in the risk levels picked in the report dialog, highest first
      // ─────────────────────────────────────────────────────────
      const combinedRows = [];

      try {
        const riskOrder = { 'Extreme Risk': 1, 'High Risk': 2, 'Moderate Risk': 3, 'Low Risk': 4, 'N/A': 5 };

        [...filteredFireRiskData]
          .filter((item) => selectedRiskLevels.includes(
            mapAdvisoryToRiskLevel(item[`advisory_${reportDateForTable}`] || "N/A")
          ))
          .sort((a, b) => {
            const aRisk = riskOrder[mapAdvisoryToRiskLevel(a[`advisory_${reportDateForTable}`] || "N/A")] || 5;
            const bRisk = riskOrder[mapAdvisoryToRiskLevel(b[`advisory_${reportDateForTable}`] || "N/A")] || 5;
            if (aRisk !== bRisk) return aRisk - bRisk;
            return (
              (a.Municipality || "").localeCompare(b.Municipality || "") ||
              (a.PostAdmin || "").localeCompare(b.PostAdmin || "") ||
              (a.Suco || "").localeCompare(b.Suco || "")
            );
          })
          .forEach((item, idx) => {
            try {
              const advisory = item[`advisory_${reportDateForTable}`] || "N/A";
              const riskLevel = mapAdvisoryToRiskLevel(advisory);
              const colRgb = colorMap[riskLevel] || [222, 222, 223];
              const pct = (v) => (v !== undefined && v !== null ? Number(v).toFixed(2) : '0.00');

              combinedRows.push([
                idx + 1,
                item.Municipality || "N/A",
                item.PostAdmin || "N/A",
                item.Suco || "N/A",
                pct(item[`low_${reportDateForTable}`]),
                pct(item[`moderate_${reportDateForTable}`]),
                pct(item[`high_${reportDateForTable}`]),
                pct(item[`extreme_${reportDateForTable}`]),
                { content: advisory, styles: { fillColor: colRgb } },
              ]);
            } catch (rowErr) {
              console.warn(`Row ${idx} skipped:`, rowErr);
            }
          });
      } catch (e) {
        console.warn("Table rows failed:", e);
      }

      if (currentY + 40 > limitY) currentY = addPageWithHeader();
      drawTwoToneHeading(pdf, currentY, "KEY TABLE : ", "HIGHEST RISK SUCOS");
      currentY += 4;

      const TABLE_HEAD_FILL = [225, 232, 242];
      // rowSpan/colSpan must sit on the cell itself, not inside `styles`.
      const headCell = (content, span = {}) => ({
        content,
        ...span,
        styles: { halign: "center", valign: "middle", fontStyle: "bold" },
      });
      autoTable(pdf, {
        startY: currentY,
        head: [
          [
            headCell("Sl No", { rowSpan: 2 }),
            headCell("Municipality", { rowSpan: 2 }),
            headCell("Post Admin", { rowSpan: 2 }),
            headCell("Suco", { rowSpan: 2 }),
            headCell(reportDateForTable, { colSpan: 5 }),
          ],
          [
            headCell("Low (%)"),
            headCell("Moderate (%)"),
            headCell("High (%)"),
            headCell("Extreme (%)"),
            headCell("Advisory"),
          ],
        ],
        body: combinedRows.length
          ? combinedRows
          : [[{ content: "No sucos in the selected risk levels", colSpan: 9, styles: { halign: "center" } }]],
        theme: "grid",
        rowPageBreak: "avoid", // never split a row across pages (leaves an empty sliver)
        headStyles: { fillColor: TABLE_HEAD_FILL, textColor: [0, 0, 0], fontSize: 8.5, cellPadding: { top: 2.2, bottom: 2.2, left: 1.5, right: 1.5 } },
        styles: { fontSize: 8.5, cellPadding: { top: 2.2, bottom: 2.2, left: 1.8, right: 1.8 }, overflow: "linebreak", textColor: [85, 85, 85], lineWidth: 0.25, lineColor: [150, 150, 150], minCellHeight: 8 },
        // Column widths (mm); total 192. Wide enough that the bold sub-headers
        // ("Low (%)", "Moderate (%)", "Advisory") stay on one line.
        columnStyles: {
          0: { cellWidth: 10, halign: "center", valign: "middle" },
          1: { cellWidth: 21, halign: "left", valign: "middle" },
          2: { cellWidth: 25, halign: "left", valign: "middle" },
          3: { cellWidth: 28, halign: "left", valign: "middle" },
          4: { cellWidth: 18, halign: "right", valign: "middle" },
          5: { cellWidth: 24, halign: "right", valign: "middle" },
          6: { cellWidth: 19, halign: "right", valign: "middle" },
          7: { cellWidth: 23, halign: "right", valign: "middle" },
          8: { cellWidth: 24, halign: "center", valign: "middle" },
        },
        // Keep rows clear of the footer bar; continuation pages get the header.
        margin: { left: 9, right: 9, top: headerHeight, bottom: pageHeight - FR_CONTENT_BOTTOM },
        didDrawPage: (data) => { if (data.pageNumber > 1) addBannerSafe(); },
      });

      // ─────────────────────────────────────────────────────────
      // FOOTER ON EVERY PAGE
      // ─────────────────────────────────────────────────────────
      try {
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          drawFireFooter(pdf, {
            message: t.footer || "Do not burn - protect our communities, forest and environment",
            pageLabel: (t.page || "Page [i] of [pageCount]")
              .replace("[i]", String(i))
              .replace("[pageCount]", String(pageCount)),
          });
        }
      } catch (e) { console.warn("Footer failed:", e); }

      // ─────────────────────────────────────────────────────────
      // SAVE
      // ─────────────────────────────────────────────────────────
      pdf.save("DRRDSS-FireRisk-Forecast-Advisory.pdf");

    } catch (fatalErr) {
      console.error("Fatal PDF error:", fatalErr);
      alert(`PDF generation failed: ${fatalErr.message || "Unknown error"}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // ─── WMS layer name for current date ─────────────────────────────────────
  const wmsLayerName = useMemo(
    () => buildWmsLayerName(latestInitTime, selectedDate),
    [latestInitTime, selectedDate]
  );

  const tableColumns = useMemo(
    () => buildTableColumns(selectedDate, legendColors),
    [selectedDate, legendColors]
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="map-container-firerisk">

        {/* Generate Report Button */}
        {hasPermission('firerisk_forecast_generate_report') && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
          <button
            onClick={handleGenerateReportClick}
            disabled={loading || !isDataReady}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              backgroundColor: loading || !isDataReady ? "#90caf9" : "#0d6efd",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: loading || !isDataReady ? "not-allowed" : "pointer",
              boxShadow: "0 2px 6px rgba(13, 110, 253, 0.3)",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
            }}
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: "#ffffff" }} />
            ) : (
              <DescriptionIcon sx={{ fontSize: 18 }} />
            )}
            Generate Report
          </button>
        </Box>
        )}

        {/* Filter Bar */}
        <Box
          sx={{
            p: 2.5,
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
            border: "1px solid #e8ecf1",
            marginBottom: "16px",
            overflow: "visible",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 22 }} />
              <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>Filter</span>
            </Box>

            <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Date:</span>
              <button
                onClick={handlePrevDate}
                disabled={selectedDateIndex === 0}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#0d6efd", opacity: selectedDateIndex === 0 ? 0.5 : 1 }}
              >
                <FontAwesomeIcon icon={faArrowCircleLeft} />
              </button>
              <div style={{ fontSize: "13px", fontWeight: 500, color: "#555", whiteSpace: "nowrap" }}>
                {forecastDates.length > 0 ? formatDateForDisplay(forecastDates[selectedDateIndex], selectedDateIndex) : '--'}
              </div>
              <button
                onClick={handleNextDate}
                disabled={selectedDateIndex === forecastDates.length - 1}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#0d6efd", opacity: selectedDateIndex === forecastDates.length - 1 ? 0.5 : 1 }}
              >
                <FontAwesomeIcon icon={faArrowCircleRight} />
              </button>
            </Box>

          </Box>
        </Box>

        {/* Map */}
        <div className="map-wrapper">
          <MapContainer
            ref={mapRef}
            center={[INITIAL_VIEW.lat, INITIAL_VIEW.lng]}
            zoom={INITIAL_VIEW.zoom}
            className="mapcontainerall"
          >
            <MapHomeControl />
            <MapBoundsUpdater bounds={mapBounds} />
            {zoomFeature && (
              <ZoomToFeature feature={{ type: "FeatureCollection", features: [zoomFeature] }} />
            )}

            <InformationFR />

            <LayersControl position="topright">

              <LayersControl.BaseLayer name="OpenStreetMap" checked>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="Map data © ECMWF - Data updation daily at 9AM (UTC)"
                />
              </LayersControl.BaseLayer>

              {municipalityData && (
                <LayersControl.Overlay name="Municipality">
                  <GeoJSON
                    data={municipalityData}
                    style={f =>
                      selectedGeojsonStyle(
                        f,
                        selectedSuco,
                        selectedSubMunicipality,
                        selectedMunicipality
                      )
                    }
                  />
                </LayersControl.Overlay>
              )}

              {subMunicipalityData && (
                <LayersControl.Overlay name="Post-Admin">
                  <GeoJSON
                    data={subMunicipalityData}
                    style={f =>
                      selectedGeojsonStyle(
                        f,
                        selectedSuco,
                        selectedSubMunicipality,
                        selectedMunicipality
                      )
                    }
                  />
                </LayersControl.Overlay>
              )}

              {sucoData && (
                <LayersControl.Overlay checked name="Suco Boundaries">
                  <GeoJSON
                    key={selectedDate}
                    data={sucoData}
                    style={getSucoStyle}
                    onEachFeature={onEachSucoFeature}
                  />
                </LayersControl.Overlay>
              )}
            </LayersControl>

            {wmsLayerName && (
              <WMSTileLayer
                key={wmsLayerName}
                url="https://drr-dss.rimes.int/fire_risk/wms"
                layers={wmsLayerName}
                format="image/png"
                transparent={true}
                version="1.1.0"
                opacity={1.0}
              />
            )}

            <MapLegendControl
              position="bottomright"
              sections={[
                {
                  shape: "square",
                  items: [
                    { label: "Extreme Risk", color: "rgb(230, 45, 58)" },
                    { label: "High Risk", color: "rgb(243, 155, 58)" },
                    { label: "Moderate Risk", color: "rgb(243, 235, 18)" },
                    { label: "Low Risk", color: "rgb(148, 203, 85)" },
                    { label: "N/A", color: "rgb(222,222,223)" },
                  ],
                },
              ]}
            />
          </MapContainer>
        </div>

        {isDataReady && (
          <div style={{ marginTop: "24px" }}>
            <CommonTable
              key={selectedDate}
              tableData={tableDataWithAdvisory}
              columns={tableColumns}
              currentTimestamp={selectedDate}
              showFilter={true}
              filterOptions={["All", "Extreme Risk", "High Risk", "Moderate Risk", "Low Risk"]}
              legendColors={legendColors}
              defaultSort={{ key: `extreme_${selectedDate}`, direction: "desc" }}
              searchableKeys={["Municipality", "PostAdmin", "Suco"]}
              tableName="FireRisk_ImpactedArea"
              searchPlaceholder="Search by Municipality, Post Admin, Suco..."
              emptyMessage="No fire risk data found for the selected filters."
            />
          </div>
        )}

        <Modal show={showLanguageModal} onHide={() => setShowLanguageModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "18px", color: "#333" }}>
              🌐 Report Options
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ textAlign: "center" }}>
            <div style={{ textAlign: "left", marginBottom: "18px" }}>
              <div style={{ fontWeight: 600, marginBottom: "8px", color: "#333" }}>
                Risk levels to include in the report table
              </div>
              {[
                { key: "Extreme Risk", color: "rgb(230, 45, 58)" },
                { key: "High Risk", color: "rgb(255, 103, 1)" },
                { key: "Moderate Risk", color: "rgb(243, 235, 18)" },
                { key: "Low Risk", color: "rgb(0, 191, 99)" },
              ].map(({ key, color }) => (
                <label
                  key={key}
                  style={{ display: "flex", alignItems: "center", gap: "8px", margin: "4px 0", cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={reportRiskLevels.includes(key)}
                    onChange={() => toggleReportRiskLevel(key)}
                  />
                  <span style={{ width: 14, height: 14, borderRadius: 3, background: color, display: "inline-block" }} />
                  {key}
                </label>
              ))}
            </div>
            <div style={{ fontWeight: 600, marginBottom: "8px", color: "#333" }}>Select report language</div>
            <Button
              onClick={() => handleLanguageSelectAndGenerate("en")}
              style={{ padding: "10px 18px", borderRadius: "6px", backgroundColor: "#007bff", color: "#fff", border: "none", cursor: "pointer", marginRight: "12px" }}
            >
              English
            </Button>
            <Button
              onClick={() => handleLanguageSelectAndGenerate("tetum")}
              style={{ padding: "10px 18px", borderRadius: "6px", backgroundColor: "#28a745", color: "#fff", border: "none", cursor: "pointer" }}
            >
              Tetum
            </Button>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLanguageModal(false)}>Cancel</Button>
          </Modal.Footer>
        </Modal>

      </div>
    </>
  );
};

export default Forecast;